import { A11yModule } from '@angular/cdk/a11y';
import { OverlayModule } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatPseudoCheckbox } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { AvatarComponent } from '@empathy/ui';

import { ValidationTextComponent } from './components/atomic/validation-text/validation-text.component';
import { GridSideFilterComponent } from './components/layout/grid-side-filter/grid-side-filter.component';
import { SidePanelComponent } from './components/layout/side-panel/side-panel.component';
import { AutocompleteComponent } from './components/molecules/autocomplete/autocomplete.component';
import { BadgeComponent } from './components/molecules/badge/badge.component';
import { BreakLineComponent } from './components/molecules/break-line/break-line.component';
import { ButtonComponent } from './components/molecules/button/button.component';
import { CheckboxGroupComponent } from './components/molecules/checkbox-group/checkbox-group.component';
import { ChipComponent } from './components/molecules/chip/chip.component';
import { ColoredCircleIconComponent } from './components/molecules/colored-circle-icon/colored-circle-icon.component';
import { CounterComponent } from './components/molecules/counter/counter.component';
import { CustomTooltipComponent } from './components/molecules/custom-tooltip/custom-tooltip.component';
import { DisplayToggleButtonsComponent } from './components/molecules/display-toggle-buttons/display-toggle-buttons.component';
import { EllipseTextComponent } from './components/molecules/ellipse-text/ellipse-text.component';
import { FlagsComponent } from './components/molecules/flags/flags.component';
import { GridFilterSkeletonComponent } from './components/molecules/grid-filter-skeleton/grid-filter-skeleton.component';
import { IconButtonComponent } from './components/molecules/icon-button/icon-button.component';
import { InfoBannerComponent } from './components/molecules/info-banner/info-banner.component';
import { InputComponent } from './components/molecules/input/input.component';
import { InputActionComponent } from './components/molecules/input-action/input-action.component';
import { InputDateComponent } from './components/molecules/input-date/input-date.component';
import { InputDateRangeComponent } from './components/molecules/input-date-range/input-date-range.component';
import { InputRadioGroupComponent } from './components/molecules/input-radio-group/input-radio-group.component';
import { InputSearchComponent } from './components/molecules/input-search/input-search.component';
import { InputVerifyComponent } from './components/molecules/input-verify/input-verify.component';
import { LoaderComponent } from './components/molecules/loader/loader.component';
import { ProgressBarComponent } from './components/molecules/progress-bar/progress-bar.component';
import { RichTextInputComponent } from './components/molecules/rich-text-input/rich-text-input.component';
import { SectionProgressComponent } from './components/molecules/section-progress/section-progress.component';
import { SelectComponent } from './components/molecules/select/select.component';
import { SelectGroupComponent } from './components/molecules/select-group/select-group.component';
import { SearchSkeletonComponent } from './components/molecules/skeleton-elements/search-skeleton/search-skeleton.component';
import { TableSkeletonComponent } from './components/molecules/skeleton-elements/table-skeleton/table-skeleton.component';
import { SlideToggleComponent } from './components/molecules/slide-toggle/slide-toggle.component';
import { TagComponent } from './components/molecules/tag/tag.component';
import { TagsInputComponent } from './components/molecules/tags-input/tags-input.component';
import { TextEditorComponent } from './components/molecules/text-editor/text-editor.component';
import { TextareaComponent } from './components/molecules/textarea/textarea.component';
import { ToggleButtonsComponent } from './components/molecules/toggle-buttons/toggle-buttons.component';
import { ToggleTextButtonComponent } from './components/molecules/toggle-text-button/toggle-text-button.component';
import { ActionMenuItemComponent } from './components/organisms/actions-menu/action-menu-item/action-menu-item.component';
import { ActionsMenuComponent } from './components/organisms/actions-menu/actions-menu.component';
import { BottomPanelEditorComponent } from './components/organisms/bottom-panel-editor/bottom-panel-editor.component';
import { CardComponent } from './components/organisms/card/card.component';
import { CardFormSelectorComponent } from './components/organisms/card-form-selector/card-form-selector.component';
import { CheckboxCardComponent } from './components/organisms/checkbox-card/checkbox-card.component';
import { ConfirmDialogComponent } from './components/organisms/confirm-dialog/confirm-dialog.component';
import { DashboardCardComponent } from './components/organisms/dashboard-card/dashboard-card.component';
import { ExpansionPanelComponent } from './components/organisms/expansion-panel/expansion-panel.component';
import { GridBulkActionsRowComponent } from './components/organisms/grid-bulk-actions-row/grid-bulk-actions-row.component';
import { GridFilterBarComponent } from './components/organisms/grid-filter-bar/grid-filter-bar.component';
import { GridSummaryBoxesComponent } from './components/organisms/grid-summary-boxes/grid-summary-boxes.component';
import { HeaderBasicDetailsComponent } from './components/organisms/header-basic-details/header-basic-details.component';
import { HeaderMainTitleComponent } from './components/organisms/header-main-title/header-main-title.component';
import { InteractiveDropdownItemComponent } from './components/organisms/interactive-dropdown/interactive-dropdown-item/interactive-dropdown-item.component';
import { InteractiveDropdownComponent } from './components/organisms/interactive-dropdown/interactive-dropdown.component';
import { SelectableCardComponent } from './components/organisms/selectable-card/selectable-card.component';
import { StateCardComponent } from './components/organisms/state-card/state-card.component';
import { AutoFocusDirective } from './directives/auto-focus.directive';
import { FaIconColorDirective } from './directives/fa-icon-color.directive';
import { CharactersLeftPipe } from './pipes/characters-left.pipe';
import { DateAdapterService } from './services/date-adapter.service';

const APP_DATE_FORMATS = {
  parse: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' }
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' }
  }
};

@NgModule({
  imports: [
    AvatarComponent,
    CKEditorModule,
    CommonModule,
    FontAwesomeModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatMenuItem,
    MatMenuTrigger,
    MatMenu,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatTabGroup,
    MatTab,
    MatTooltipModule,
    MatToolbar,
    OverlayModule,
    ProgressBarComponent,
    ReactiveFormsModule,
    SectionProgressComponent,
    TextFieldModule,
    A11yModule,
    MatPseudoCheckbox
  ],
  declarations: [
    ActionMenuItemComponent,
    ActionsMenuComponent,
    AutocompleteComponent,
    AutoFocusDirective,
    BadgeComponent,
    BottomPanelEditorComponent,
    ButtonComponent,
    CardComponent,
    CardFormSelectorComponent,
    CharactersLeftPipe,
    CheckboxCardComponent,
    CheckboxGroupComponent,
    ChipComponent,
    DashboardCardComponent,
    ColoredCircleIconComponent,
    ConfirmDialogComponent,
    CounterComponent,
    CustomTooltipComponent,
    DisplayToggleButtonsComponent,
    EllipseTextComponent,
    ExpansionPanelComponent,
    FaIconColorDirective,
    FlagsComponent,
    BreakLineComponent,
    GridBulkActionsRowComponent,
    GridFilterBarComponent,
    GridFilterSkeletonComponent,
    GridSideFilterComponent,
    GridSummaryBoxesComponent,
    HeaderBasicDetailsComponent,
    HeaderMainTitleComponent,
    IconButtonComponent,
    InfoBannerComponent,
    InputComponent,
    InputActionComponent,
    InputDateComponent,
    InputDateRangeComponent,
    InputRadioGroupComponent,
    InputSearchComponent,
    InputVerifyComponent,
    InteractiveDropdownComponent,
    InteractiveDropdownItemComponent,
    LoaderComponent,
    RichTextInputComponent,
    SearchSkeletonComponent,
    SelectComponent,
    SelectableCardComponent,
    SelectGroupComponent,
    SidePanelComponent,
    SlideToggleComponent,
    StateCardComponent,
    TableSkeletonComponent,
    TagsInputComponent,
    TextareaComponent,
    TextEditorComponent,
    TagComponent,
    ToggleButtonsComponent,
    ToggleTextButtonComponent,
    ValidationTextComponent
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-IE' },
    { provide: DateAdapter, useClass: DateAdapterService, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  exports: [
    ActionsMenuComponent,
    ActionMenuItemComponent,
    AutocompleteComponent,
    BadgeComponent,
    BottomPanelEditorComponent,
    ButtonComponent,
    CardComponent,
    CardFormSelectorComponent,
    CheckboxCardComponent,
    CheckboxGroupComponent,
    ChipComponent,
    DashboardCardComponent,
    SectionProgressComponent,
    ColoredCircleIconComponent,
    CounterComponent,
    CustomTooltipComponent,
    DisplayToggleButtonsComponent,
    EllipseTextComponent,
    ExpansionPanelComponent,
    FaIconColorDirective,
    FlagsComponent,
    BreakLineComponent,
    GridBulkActionsRowComponent,
    GridFilterBarComponent,
    GridFilterSkeletonComponent,
    GridSideFilterComponent,
    GridSummaryBoxesComponent,
    HeaderBasicDetailsComponent,
    HeaderMainTitleComponent,
    IconButtonComponent,
    InfoBannerComponent,
    InputComponent,
    InputActionComponent,
    InputDateComponent,
    InputDateRangeComponent,
    InputRadioGroupComponent,
    InputSearchComponent,
    InputVerifyComponent,
    InteractiveDropdownComponent,
    InteractiveDropdownItemComponent,
    LoaderComponent,
    ProgressBarComponent,
    RichTextInputComponent,
    SearchSkeletonComponent,
    SelectComponent,
    SelectableCardComponent,
    SelectGroupComponent,
    SidePanelComponent,
    SlideToggleComponent,
    StateCardComponent,
    ValidationTextComponent,
    TableSkeletonComponent,
    TagComponent,
    TagsInputComponent,
    TextareaComponent,
    TextEditorComponent,
    ToggleButtonsComponent,
    ToggleTextButtonComponent
  ]
})
export class UiAdminModule {}
